// MSAL config for the CXHubV2 SPA. clientId and tenant are PUBLIC Entra
// identifiers (not secrets). redirectUri is derived from the current origin so
// the same build works in prod, staging, every Lab and local dev.
//
// The client id is NOT built in. The SPA asks the server it was loaded from
// (GET /api/config, server/src/routes/client-config.js), which answers with
// the same ENTRA_CLIENT_ID it validates tokens against. So prod signs in to
// the prod app registration and staging and the Labs to their own, and a
// token captured on a Lab is not valid in prod (security audit 2026-10-05,
// O-1). main.jsx loads this before it creates the MSAL instance.

const CONFIG_PATH = '/api/config'

// Placeholder identifiers for the Playwright build (VITE_E2E=true), which
// never signs in: App.jsx skips the MSAL login gate and the tests mock the
// API. MSAL still needs a well-formed config to construct.
export const E2E_AUTH_CONFIG = {
  entraClientId: '00000000-0000-0000-0000-000000000000',
  entraTenantId: '00000000-0000-0000-0000-000000000000',
}

// Access token request for our API (the exposed scope). Filled in by
// buildMsalConfig() before anything renders; the object itself never changes,
// so App.jsx can import it once.
export const apiRequest = { scopes: [] }

// Fetches the server's sign-in identifiers. A plain fetch, not api.js: there
// is no token yet, and the route is public. `fallback` is used only when the
// server says auth is not configured (local development with
// AUTH_DISABLED=true); main.jsx passes it in dev builds only.
export async function loadAuthConfig({ fetchImpl = fetch, fallback = null } = {}) {
  let res
  try {
    res = await fetchImpl(CONFIG_PATH, { cache: 'no-store', headers: { Accept: 'application/json' } })
  } catch (err) {
    if (fallback) return fallback
    throw new Error(`Could not reach ${CONFIG_PATH}: ${err.message}`)
  }
  if (!res.ok) {
    if (fallback) return fallback
    throw new Error(`${CONFIG_PATH} answered ${res.status}`)
  }
  const body = await res.json()
  if (!body?.entraClientId || !body?.entraTenantId) {
    throw new Error(`${CONFIG_PATH} did not name a client id and tenant`)
  }
  return { entraClientId: body.entraClientId, entraTenantId: body.entraTenantId }
}

// Builds the MSAL config and fills in apiRequest's scope from the loaded ids.
export function buildMsalConfig({ entraClientId, entraTenantId }) {
  apiRequest.scopes = [`api://${entraClientId}/access_as_user`]
  return {
    auth: {
      clientId: entraClientId,
      authority: `https://login.microsoftonline.com/${entraTenantId}`,
      redirectUri: window.location.origin + '/',
      postLogoutRedirectUri: window.location.origin + '/',
    },
    cache: { cacheLocation: 'sessionStorage' },
  }
}

// Deep-link preservation across MSAL's redirect flow: redirectUri above is
// pinned to the bare origin (it must exactly match a URI registered on the
// Entra app registration — every possible /contact/<n> path can't be
// pre-registered), so a visitor who opens e.g. /contact/42 while logged out
// gets bounced through login.microsoftonline.com and lands back at plain
// "/", losing the path entirely. sessionStorage survives that full-page
// round trip (in-memory JS state does not), so the intended path is
// captured here BEFORE the redirect fires and restored once Shell mounts
// authenticated (App.jsx's boot effect, via consumeDeepLinkPath()).
const DEEP_LINK_KEY = 'cxhub:deepLinkPath'

// Call at module-evaluation time, before MsalAuthenticationTemplate's own
// redirect-trigger effect can run — a React effect would be too late.
// App.jsx is imported statically by main.jsx, so this still runs before the
// /api/config fetch and before MSAL exists.
export function captureDeepLinkPath() {
  try {
    const { pathname } = window.location
    if (pathname && pathname !== '/') sessionStorage.setItem(DEEP_LINK_KEY, pathname)
  } catch {
    // Private-browsing/storage-disabled: the deep link is simply lost, same
    // as any other environment where sessionStorage isn't available — not
    // fatal, the app still boots to its normal default view.
  }
}

// Read-once: returns the captured path (or null when there wasn't one —
// either a normal login with no deep link, or the visitor was already
// authenticated and never redirected at all) and clears it, so a later
// login doesn't replay a stale path from a previous session.
export function consumeDeepLinkPath() {
  try {
    const path = sessionStorage.getItem(DEEP_LINK_KEY)
    sessionStorage.removeItem(DEEP_LINK_KEY)
    return path
  } catch {
    return null
  }
}
